import { useState, useEffect, useRef, useCallback, lazy, Suspense } from 'react';
import Layout, { type LayoutChildProps } from './components/Layout';
import ChatRoom from './components/ChatRoom';
import { trackTabChange } from './analytics';
import { useSessionTimeout } from './hooks/useSessionTimeout';
import { useVisualViewport } from './hooks/useVisualViewport';
import { SESSION_WARNING_MS, SESSION_TIMEOUT_MS, LOGIN_PATH } from './constants';

// Heavy tabs are lazy-loaded so they don't bloat the initial bundle
const NetworkingView = lazy(() => import('./components/NetworkingView'));
const EducationView  = lazy(() => import('./components/EducationView'));
const ProfileView    = lazy(() => import('./components/ProfileView'));
import { getMessagesForChat, createMessageThenStreamResponse } from './chatservice';
import { createChat } from './chatmetadata';
import { fetchCurrentUserWithPlan, type BackendUserWithPlan } from './userService';
import { apiClient } from './apiClient';
import { moveChatToProject } from './chatservice';
import { useToastContext } from './components/ToastProvider';
import CheckoutSuccessModal from './components/CheckoutSuccessModal';
import './App.css';

export type TabType = 'assistant' | 'networking' | 'education' | 'profile';
export type EducationSubTab = 'bootcamp' | 'coaching';
export type NetworkingSubTab = 'connections' | 'requests' | 'discover' | 'groups';

export type ConnectionStatus = 'connected' | 'pending_sent' | 'pending_received' | 'none';

export type Professional = {
    id: string;
    name: string;
    email: string;
    role: string;
    company: string;
    location: string;
    avatar: string;
    online: boolean;
    skills: string[];
    connectionStatus: ConnectionStatus;
};

export type GroupChat = {
    id: string;
    name: string;
    members: Professional[];
    lastMessage?: string;
    lastMessageTime?: string;
    unreadCount?: number;
};

export type MessageFile = {
    id: string;
    file: string;
    thumbnail?: string;
    original_name: string;
    file_type: string;
};

export type Message = {
    id: string;
    order: number;
    role: 'user' | 'assistant' | 'tool';
    content: string;
    tool_calls?: ToolCall[];
    tool_call_id?: string;
    streaming?: boolean;
    files?: MessageFile[];
};

export type ToolCall = {
    id: string;
    type: string;
    function: {
        name: string;
        arguments: string;
    };
};

// ── localStorage helpers for navigation persistence ─────────────────
function readLS<T>(key: string, fallback: T): T {
    try {
        const raw = localStorage.getItem(key);
        return raw !== null ? (JSON.parse(raw) as T) : fallback;
    } catch { return fallback; }
}
function writeLS(key: string, value: unknown) {
    try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* quota */ }
}

function App() {
    useVisualViewport();
    const { addToast } = useToastContext();
    const [activeTab, setActiveTab] = useState<TabType>(() => readLS('nav_activeTab', 'assistant'));
    const [educationSubTab, setEducationSubTab] = useState<EducationSubTab>(() => readLS('nav_educationSubTab', 'bootcamp'));
    const [networkingSubTab, setNetworkingSubTab] = useState<NetworkingSubTab>(() => readLS('nav_networkingSubTab', 'connections'));
    const [selectedChatId, setSelectedChatId] = useState<string | null>(() => readLS('nav_selectedChatId', null));
    const [selectedProjectId, setSelectedProjectId] = useState<string | null>(() => readLS('nav_selectedProjectId', null));
    const [dmUser, setDmUser] = useState<Professional | null>(null);
    const [activeGroupChat, setActiveGroupChat] = useState<GroupChat | null>(null);
    const [messages, setMessages] = useState<Message[]>([]);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [userData, setUserData] = useState<BackendUserWithPlan | null>(null);
    const [sidebarProjects, setSidebarProjects] = useState<{ id: string; name: string }[]>([]);
    const skipLoadForChatIdRef = useRef<string | null>(null);
    const [showSessionWarning, setShowSessionWarning] = useState(false);

    // Session timeout: warn user 5 min before JWT likely expires
    const handleSessionWarning = useCallback(() => {
        setShowSessionWarning(true);
    }, []);
    // Persist navigation state to localStorage
    useEffect(() => { writeLS('nav_activeTab', activeTab); }, [activeTab]);
    useEffect(() => { writeLS('nav_educationSubTab', educationSubTab); }, [educationSubTab]);
    useEffect(() => { writeLS('nav_networkingSubTab', networkingSubTab); }, [networkingSubTab]);
    useEffect(() => { writeLS('nav_selectedChatId', selectedChatId); }, [selectedChatId]);
    useEffect(() => { writeLS('nav_selectedProjectId', selectedProjectId); }, [selectedProjectId]);

    const handleSessionTimeout = useCallback(() => {
        setShowSessionWarning(false);
        window.location.href = LOGIN_PATH;
    }, []);
    useSessionTimeout({
        warningAfterMs: SESSION_WARNING_MS,
        logoutAfterMs: SESSION_TIMEOUT_MS,
        onWarning: handleSessionWarning,
        onTimeout: handleSessionTimeout,
        enabled: !!userData,  // only when logged in
    });

    // Listen for sidebar broadcasting its project list
    useEffect(() => {
        const handler = (e: Event) => {
            const detail = (e as CustomEvent).detail;
            setSidebarProjects(detail.projects || []);
        };
        window.addEventListener('sidebar-projects-loaded', handler);
        return () => window.removeEventListener('sidebar-projects-loaded', handler);
    }, []);

    const handleTabChange = (tab: TabType, subTab?: string) => {
        setActiveTab(tab);
        trackTabChange(subTab ? `${tab}:${subTab}` : tab);
        if (tab === 'education' && subTab) {
            setEducationSubTab(subTab as EducationSubTab);
        } else if (tab === 'networking' && subTab) {
            setNetworkingSubTab(subTab as NetworkingSubTab);
        }
    };

    // Load initial user data
    useEffect(() => {
        async function loadUser() {
            const data = await fetchCurrentUserWithPlan();
            setUserData(data);
        }
        loadUser();
    }, []);

    // After Stripe checkout, the success handler redirects back here
    // with ?checkout=success. Detect it, refetch user data so the new
    // plan/access is reflected immediately, then clean up the URL.
    const [showCheckoutSuccess, setShowCheckoutSuccess] = useState(false);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('checkout') === 'success') {
            window.history.replaceState({}, '', window.location.pathname);
            (async () => {
                const data = await fetchCurrentUserWithPlan();
                setUserData(data);
                setShowCheckoutSuccess(true);
            })();
        }
    }, []);

    // When PaywallGate or Sidebar dispatches 'open-plans', switch to the
    // profile tab. ProfileView then listens for the same event and opens
    // the plans drawer.
    useEffect(() => {
        const onOpenPlans = () => setActiveTab('profile');
        window.addEventListener('open-plans', onOpenPlans);
        return () => window.removeEventListener('open-plans', onOpenPlans);
    }, []);

    // Also refetch user data when the tab regains focus — catches plan
    // changes that happened while the user was on Stripe's checkout page.
    useEffect(() => {
        const onFocus = () => {
            fetchCurrentUserWithPlan().then(setUserData).catch(() => {});
        };
        window.addEventListener('focus', onFocus);
        return () => window.removeEventListener('focus', onFocus);
    }, []);

    // Load messages when a chat is selected
    useEffect(() => {
        if (!selectedChatId) {
            setMessages([]);
            setLoadingMessages(false);
            return;
        }

        // When we create a new projectless chat and immediately start streaming,
        // we don't want to fetch messages in parallel (it can race and overwrite UI).
        if (skipLoadForChatIdRef.current === selectedChatId) {
            skipLoadForChatIdRef.current = null;
            return;
        }

        let cancelled = false;
        async function loadMessages() {
            setLoadingMessages(true);
            try {
                const chatMessages = await getMessagesForChat(selectedChatId!);
                if (!cancelled) setMessages(chatMessages);
            } catch (error) {
                if (!cancelled) console.error('Failed to load messages:', error);
            } finally {
                if (!cancelled) setLoadingMessages(false);
            }
        }
        loadMessages();
        return () => { cancelled = true; };
    }, [selectedChatId]);

    const handleSendMessage = async (content: string, file?: File | null) => {
        try {
            let chatId = selectedChatId;

            if (!chatId && selectedProjectId) {
                // Project chat: create on first message with auto-naming
                // Note: We currently create chat with text only. 
                // If there's a file, it will be sent in the subsequent stream call.
                const created = await createChat(selectedProjectId, null, content);
                chatId = created.id;

                skipLoadForChatIdRef.current = chatId;
                setSelectedChatId(chatId);

                // Let the RightPanel add the new chat under the project
                window.dispatchEvent(new CustomEvent('project-chat-created', {
                    detail: { projectId: selectedProjectId, chat: created },
                }));
            } else if (!chatId) {
                // Projectless direct chat: create on first message with auto-naming
                const created = await createChat(null, null, content);
                chatId = created.id;

                setSelectedProjectId(null);
                                skipLoadForChatIdRef.current = chatId;
                setSelectedChatId(chatId);

                window.dispatchEvent(new CustomEvent('direct-chat-created', { detail: created }));
            }

            // Bump chat to top based on context
            if (chatId && selectedProjectId) {
                window.dispatchEvent(new CustomEvent('project-chat-touched', { detail: { projectId: selectedProjectId, chatId } }));
            } else if (chatId && selectedProjectId === null) {
                window.dispatchEvent(new CustomEvent('direct-chat-touched', { detail: { chatId } }));
            }

            await createMessageThenStreamResponse(chatId, content, setMessages, file);

            // Refresh user data so token usage is up-to-date in profile
            fetchCurrentUserWithPlan().then(setUserData).catch(() => {});
        } catch (error) {
            console.error('Failed to send message:', error);
            addToast('Failed to send message. Please try again.', 'error');
        }
    };

    const handleNewDirectChat = () => {
                setSelectedChatId(null);
        setSelectedProjectId(null);
        setMessages([]);
        setLoadingMessages(false);
    };

    const handleMoveToProject = async (targetProjectId: string | null) => {
        if (!selectedChatId) {
            // New chat — just update the selected project so next message creates under it
            setSelectedProjectId(targetProjectId);
            return;
        }
        // Existing chat — call API to move it
        try {
            await moveChatToProject(selectedChatId, targetProjectId);
            setSelectedProjectId(targetProjectId);
            // Notify sidebar to refresh
            window.dispatchEvent(new CustomEvent('chat-moved', {
                detail: { chatId: selectedChatId, fromProjectId: selectedProjectId, toProjectId: targetProjectId }
            }));
        } catch (error) {
            console.error('Failed to move chat:', error);
            addToast('Failed to move chat to project', 'error');
        }
    };

    const handleCreateProjectFromChat = () => {
        // Dispatch event that Sidebar listens to — opens the create project modal
        // After project is created, the sidebar calls back with the new project ID
        window.dispatchEvent(new CustomEvent('open-create-project-modal', {
            detail: { chatId: selectedChatId }
        }));
    };

    const handleChatSelect = (_projectId: string, chatId: string) => {
        const normalizedProjectId = _projectId ? _projectId : null;
        setSelectedProjectId(normalizedProjectId);
        setActiveTab('assistant');

        // Always close the right panel when choosing a chat.
        
        // Empty chatId means "new chat in project" — clear state and wait for first message.
        if (!chatId) {
            setSelectedChatId(null);
            setMessages([]);
            setLoadingMessages(false);
            return;
        }

        // If the user clicks the chat they're already in, don't reload.
        if (chatId === selectedChatId) return;

        // Clear old messages immediately so we don't show the previous chat while loading.
        setMessages([]);
        setLoadingMessages(true);
        setSelectedChatId(chatId);
    };

    const handleMessageUser = (professional: Professional) => {
        setDmUser(professional);
        setActiveGroupChat(null);
    };

    const handleBackFromDm = () => {
        setDmUser(null);
        setActiveGroupChat(null);
    };

    const handleOpenGroupChat = (group: GroupChat) => {
        setActiveGroupChat(group);
        setDmUser(null);
    };

    const handleCreateGroup = async (name: string, members: Professional[]) => {
        try {
            const response = await apiClient.post('/api/networking/groups/', {
                name,
                member_ids: members.map(m => m.id),
            });
            const data = response.data;
            const newGroup: GroupChat = {
                id: String(data.id ?? `g${Date.now()}`),
                name: String(data.name ?? name),
                members: Array.isArray(data.members) ? data.members.map((m: Record<string, unknown>) => ({
                    id: String(m.id ?? ''),
                    name: String(m.name ?? ''),
                    email: String(m.email ?? ''),
                    role: String(m.role ?? ''),
                    company: String(m.company ?? ''),
                    location: String(m.location ?? ''),
                    avatar: String(m.avatar ?? ''),
                    online: Boolean(m.online),
                    skills: Array.isArray(m.skills) ? m.skills.map(String) : [],
                    connectionStatus: (String(m.connection_status ?? m.connectionStatus ?? 'none')) as ConnectionStatus,
                    connectionId: (m.connection_id ?? m.connectionId ?? null) as string | null,
                })) : members,
            };
            setActiveGroupChat(newGroup);
        } catch (error) {
            console.error('Failed to create group:', error);
            // Fallback: open with provided data so UI doesn't break
            const newGroup: GroupChat = {
                id: `g${Date.now()}`,
                name,
                members,
            };
            setActiveGroupChat(newGroup);
        }
    };

    // Render the active view based on selected tab
    const renderActiveView = ({ showMenu, onMenuClick }: LayoutChildProps) => {
        switch (activeTab) {
            case 'assistant':
                return (
                    <ChatRoom
                        messages={messages}
                        placeholder="Ask Chiefy anything about property development..."
                        title={selectedChatId ? "Chiefy Assistant" : "New Chat"}
                        isLoadingMessages={loadingMessages}
                        activeTab={activeTab}
                        onMenuClick={onMenuClick}
                        showMenu={showMenu}
                        chatId={selectedChatId || undefined}
                        projectId={selectedProjectId || undefined}
                        projects={sidebarProjects}
                        onMoveToProject={handleMoveToProject}
                        onCreateProject={handleCreateProjectFromChat}
                        onSendMessage={handleSendMessage}
                        userData={userData}
                    />
                );
            case 'networking':
                // Show group chat if a group is selected
                if (activeGroupChat) {
                    return (
                        <ChatRoom
                            messages={[]}
                            placeholder={`Message ${activeGroupChat.name}...`}
                            title={activeGroupChat.name}
                            subtitle={`${activeGroupChat.members.length} members`}
                            activeTab={activeTab}
                            onMenuClick={onMenuClick}
                            showMenu={showMenu}
                            onBack={handleBackFromDm}
                            groupChat={{
                                name: activeGroupChat.name,
                                memberCount: activeGroupChat.members.length,
                                members: activeGroupChat.members,
                            }}
                            userData={userData}
                        />
                    );
                }
                // Show DM chat if a user is selected
                if (dmUser) {
                    return (
                        <ChatRoom
                            messages={[]}
                            placeholder={`Message ${dmUser.name}...`}
                            title={dmUser.name}
                            subtitle={`${dmUser.role} at ${dmUser.company}`}
                            activeTab={activeTab}
                            onMenuClick={onMenuClick}
                            showMenu={showMenu}
                            onBack={handleBackFromDm}
                            chatPartner={{
                                name: dmUser.name,
                                avatar: dmUser.avatar,
                                online: dmUser.online,
                            }}
                            userData={userData}
                        />
                    );
                }
                return (
                    <Suspense fallback={<div className="suspense-loader">Loading…</div>}>
                        <NetworkingView
                            onMenuClick={onMenuClick}
                            showMenu={showMenu}
                            onMessageUser={handleMessageUser}
                            onOpenGroupChat={handleOpenGroupChat}
                            onCreateGroup={handleCreateGroup}
                            initialSubTab={networkingSubTab}
                        />
                    </Suspense>
                );
            case 'education':
                return (
                    <Suspense fallback={<div className="suspense-loader">Loading…</div>}>
                        <EducationView
                            onMenuClick={onMenuClick}
                            showMenu={showMenu}
                            initialSubTab={educationSubTab}
                        />
                    </Suspense>
                );
            case 'profile':
                return (
                    <Suspense fallback={<div className="suspense-loader">Loading…</div>}>
                        <ProfileView
                            onMenuClick={onMenuClick}
                            showMenu={showMenu}
                            userData={userData}
                        />
                    </Suspense>
                );
            default:
                // Safety net: unrecognised tab falls back to the assistant view
                // so the user never sees a blank screen.
                return (
                    <ChatRoom
                        messages={messages}
                        placeholder="Ask Chiefy anything about property development..."
                        title="New Chat"
                        isLoadingMessages={loadingMessages}
                        activeTab={activeTab}
                        onMenuClick={onMenuClick}
                        showMenu={showMenu}
                        chatId={selectedChatId || undefined}
                        projectId={selectedProjectId || undefined}
                        projects={sidebarProjects}
                        onMoveToProject={handleMoveToProject}
                        onCreateProject={handleCreateProjectFromChat}
                        onSendMessage={handleSendMessage}
                        userData={userData}
                    />
                );
        }
    };

    return (
        <>
            {/* Session timeout warning — dismissible, resets on interaction */}
            {showSessionWarning && (
                <div className="session-warning" role="alert">
                    <span>Your session is about to expire.</span>
                    <button type="button" onClick={() => {
                        setShowSessionWarning(false);
                        // Touching the page resets the timeout hook automatically
                    }}>
                        Stay signed in
                    </button>
                </div>
            )}

            <Layout
                activeTab={activeTab}
                onTabChange={handleTabChange}
                educationSubTab={educationSubTab}
                networkingSubTab={networkingSubTab}
                userData={userData}
                onChatSelect={handleChatSelect}
                onNewDirectChat={handleNewDirectChat}
                selectedChatId={selectedChatId || undefined}
                selectedProjectId={selectedProjectId || undefined}
            >
                {(props) => renderActiveView(props)}
            </Layout>

            {/* Checkout success celebration */}
            <CheckoutSuccessModal
                open={showCheckoutSuccess}
                onClose={() => setShowCheckoutSuccess(false)}
                planName={userData?.active_plan?.plan?.name}
            />
        </>
    );
}

export default App;
